import Vue from 'vue'
import Router from 'vue-router'

// Containers
const TheContainer = () => import('@/containers/TheContainer')

// Views
const Dashboard = () => import('@/views/Dashboard')

// Views - Pages
const Page403 = () => import('@/views/pages/Page403')
const Page404 = () => import('@/views/pages/Page404')
const Page500 = () => import('@/views/pages/Page500')
const Login = () => import('@/views/pages/Login')
const Register = () => import('@/views/pages/Register')

const UserList = () => import('@/views/admins/UserList')
const User = () => import('@/views/admins/User')

const VehicleList = () => import('@/views/agents/VehicleList')
const Vehicle = () => import('@/views/agents/Vehicle')

const AdvertisementList = () => import('@/views/marketing/AdvertisementList')
const Advertisement = () => import('@/views/marketing/Advertisement')

const ShowroomList = () => import('@/views/admins/ShowroomList')
const Showroom = () => import('@/views/admins/Showroom')

const ConfirmBuyList = () => import('@/views/admins/ConfirmBuyList')
const ConfirmBuy = () => import('@/views/admins/ConfirmBuy')

const Profile = () => import('@/views/general/Profile')

//
const StockList = () => import('@/views/admins/StockList')
const Stock = () => import('@/views/admins/Stock')
const StockBulkImport = () => import('@/views/admins/StockBulkImport')

const DisburstmentList = () => import('@/views/admins/DisburstmentList')
const Disburstment = () => import('@/views/admins/Disburstment')

const Company = () => import('@/views/admins/Company')
const CompanyEdit = () => import('@/views/admins/CompanyEdit')

const RoleList = () => import('@/views/admins/RoleList')
const Role = () => import('@/views/admins/Role')

const AuditLogList = () => import('@/views/admins/AuditLogList')

const BrandList = () => import('@/views/admins/BrandList')
const Brand = () => import('@/views/admins/Brand')

const BankList = () => import('@/views/admins/BankList')
const Bank = () => import('@/views/admins/Bank')

const SubCompanyList = () => import('@/views/admins/SubCompanyList')
const SubCompany = () => import('@/views/admins/SubCompany')


const ForwarderList = () => import('@/views/admins/ForwarderList')
const Forwarder = () => import('@/views/admins/Forwarder')

const SupplierList = () => import('@/views/admins/SupplierList')
const Supplier = () => import('@/views/admins/Supplier')


const CustomerList = () => import('@/views/admins/CustomerList')
const Customer = () => import('@/views/admins/Customer')

const SalesReportList = () => import('@/views/admins/SalesReportList')
const SalesReport = () => import('@/views/admins/SalesReport')
const LjReportList = () => import('@/views/admins/LjReportList')
const LjReport = () => import('@/views/admins/LjReport')


Vue.use(Router)

const router = new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'active',
  scrollBehavior: () => ({ y: 0 }),
  routes: configRoutes()
})


router.beforeEach((to, from, next) => {
  // Check if the user is authenticated
  const isAuth = auth.check();

  // Allow access to public pages
  const publicPages = ['Login', 'Register', 'Page404', 'Page403', 'Page500'];
  if (publicPages.includes(to.name)) {
    next();
    return;
  }

  // Redirect to login if not authenticated
  if (!isAuth) {
    next({ name: 'Login' });
    return;
  }

  // User is authenticated - check permissions
  const moduleCode = to.meta?.moduleCode;
  const subModuleCode = to.meta?.subModuleCode;

  // If no module code is specified, allow access (e.g., Profile)
  if (!moduleCode) {
    next();
    return;
  }

  // Check if user is Super Admin (bypass all permission checks)
  if (auth.isSuperAdmin()) {
    next();
    return;
  }

  // Check if user can access this route
  const canAccess = auth.canAccessRoute(moduleCode, subModuleCode);

  if (!canAccess) {
    // Prevent infinite redirect loop - if coming from Page403, don't redirect again
    if (from.name === 'Page403') {
      console.error('Already on 403 page, stopping redirect loop');
      next(false); // Cancel navigation
      return;
    }

    // User doesn't have permission - find first allowed route or show 403
    const firstAllowedRoute = findFirstAllowedRoute(router.options.routes);

    if (firstAllowedRoute && firstAllowedRoute.path !== to.path) {
      console.warn(`Access denied to ${to.path}. Redirecting to ${firstAllowedRoute.path}`);
      next({ path: firstAllowedRoute.path, replace: true });
    } else {
      console.warn(`Access denied to ${to.path}. No accessible routes found.`);
      next({ name: 'Page403', replace: true });
    }
    return;
  }

  // User has permission
  next();
})

/**
 * Find the first route the user has permission to access
 * @param {Array} routes - Router routes
 * @returns {Object|null} First accessible route or null
 */
function findFirstAllowedRoute(routes) {
  console.log('=== Finding first allowed route ===');

  for (const route of routes) {
    const moduleCode = route.meta?.moduleCode;
    const subModuleCode = route.meta?.subModuleCode;

    // Skip routes with dynamic parameters (e.g., `:id`)
    if (route.path.includes(':')) {
      continue;
    }

    // Skip public pages
    const publicPages = ['Login', 'Register', 'Page404', 'Page403', 'Page500'];
    if (publicPages.includes(route.name)) {
      continue;
    }

    // If route has no module code, it's accessible (e.g., Dashboard, Profile)
    if (!moduleCode) {
      console.log(`✓ Found accessible route without moduleCode: ${route.path} (${route.name})`);
      return route;
    }

    // Check if user has permission to access this route
    const hasAccess = auth.canAccessRoute(moduleCode, subModuleCode);
    if (hasAccess) {
      console.log(`✓ Found accessible route: ${route.path} (${route.name}) - Module: ${moduleCode}`);
      return route;
    }

    // Recursively check children
    if (route.children) {
      const allowedChild = findFirstAllowedRoute(route.children);
      if (allowedChild) {
        return allowedChild;
      }
    }
  }

  console.warn('✗ No accessible routes found');
  return null;
}

export default router

function configRoutes() {
  return [
    {
      path: '/',
      redirect: '/dashboard',
      name: 'Home',
      component: TheContainer,
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: Dashboard
          // No moduleCode - accessible to all authenticated users
        },
        {
          path: 'agents',
          redirect: '/agents/vehicleList',
          name: 'Agents',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'Vehicle/:id',
              name: 'VehicleById',
              component: Vehicle,
              meta: {
                moduleCode: 'VEHICLE'
              }
            },
            {
              path: 'Vehicle',
              name: 'Vehicle',
              component: Vehicle,
              meta: {
                moduleCode: 'VEHICLE'
              }
            },
            {
              path: '/agents/VehicleList',
              name: 'VehicleList',
              component: VehicleList,
              meta: {
                moduleCode: 'VEHICLE'
              }
            },
          ]
        },
        {
          path: 'marketing',
          redirect: '/marketing/AdvertisementList',
          name: 'Marketing',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'Advertisement/:id',
              name: 'AdvertisementById',
              component: Advertisement,
              meta: {
                moduleCode: 'ADVERTISEMENT'
              }
            },
            {
              path: 'Advertisement',
              name: 'Advertisement',
              component: Advertisement,
              meta: {
                moduleCode: 'ADVERTISEMENT'
              }
            },
            {
              path: '/marketing/AdvertisementList',
              name: 'AdvertisementList',
              component: AdvertisementList,
              meta: {
                moduleCode: 'ADVERTISEMENT'
              }
            },
          ]
        },

        {
          path: 'admins',
          redirect: '/admins/userList',
          name: 'User',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'User/:id',
              name: 'UserById',
              component: User,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'USER'
              }
            },
            {
              path: 'User',
              name: 'User',
              component: User,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'USER'
              }
            },

            {
              path: 'UserList',
              name: 'UserList',
              component: UserList,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'USER'
              }
            },

            {
              path: 'role/:id',
              name: 'roleById',
              component: Role,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'ROLES'
              }
            },
            {
              path: 'role',
              name: 'role',
              component: Role,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'ROLES'
              }
            },
            {
              path: 'roleList',
              name: 'roleList',
              component: RoleList,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'ROLES'
              }
            },
            {
              path: 'AuditLogList',
              name: 'AuditLogList',
              component: AuditLogList,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'AUDIT_LOGS'
              }
            },
            {
              path: 'stock/:id',
              name: 'stockById',
              component: Stock,
              meta: {
                moduleCode: 'STOCKS'
              }
            },
            {
              path: 'stock',
              name: 'stock',
              component: Stock,
              meta: {
                moduleCode: 'STOCKS'
              }
            },
            {
              path: 'stockList',
              name: 'stockList',
              component: StockList,
              meta: {
                moduleCode: 'STOCKS'
              }
            },
            {
              path: 'stockBulkImport',
              name: 'stockBulkImport',
              component: StockBulkImport,
              meta: {
                moduleCode: 'STOCKS'
              }
            },

            {
              path: 'disburstment/:id',
              name: 'disburstmentById',
              component: Disburstment,
              meta: {
                moduleCode: 'STOCKS'
              }
            },
            {
              path: 'disburstmentList',
              name: 'disburstmentList',
              component: DisburstmentList,
              meta: {
                moduleCode: 'STOCKS'
              }
            },
            {
              path: 'companyEdit',
              name: 'CompanyEdit',
              component: CompanyEdit,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'COMPANY'
              }
            },
            {
              path: 'company',
              name: 'company',
              component: Company,
              meta: {
                moduleCode: 'ADMINISTRATION',
                subModuleCode: 'COMPANY'
              }
            },

            {
              path: 'Brand/:id',
              name: 'BrandById',
              component: Brand,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'BRANDS'
              }
            },
            {
              path: 'Brand',
              name: 'Brand',
              component: Brand,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'BRANDS'
              }
            },
            {
              path: 'BrandList',
              name: 'BrandList',
              component: BrandList,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'BRANDS'
              }
            },

            {
              path: 'Bank/:id',
              name: 'BankById',
              component: Bank,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'BANKS'
              }
            },
            {
              path: 'Bank',
              name: 'Bank',
              component: Bank,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'BANKS'
              }
            },
            {
              path: 'BankList',
              name: 'BankList',
              component: BankList,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'BANKS'
              }
            },

            //
            {
              path: 'SubCompany/:id',
              name: 'SubCompanyById',
              component: SubCompany,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SUB_COMPANIES'
              }
            },
            {
              path: 'SubCompany',
              name: 'SubCompany',
              component: SubCompany,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SUB_COMPANIES'
              }
            },
            {
              path: 'SubCompanyList',
              name: 'SubCompanyList',
              component: SubCompanyList,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SUB_COMPANIES'
              }
            },

            //
            {
              path: 'Forwarder/:id',
              name: 'ForwarderById',
              component: Forwarder,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'FORWARDERS'
              }
            },
            {
              path: 'Forwarder',
              name: 'Forwarder',
              component: Forwarder,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'FORWARDERS'
              }
            },
            {
              path: 'ForwarderList',
              name: 'ForwarderList',
              component: ForwarderList,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'FORWARDERS'
              }
            },


            {
              path: 'Supplier/:id',
              name: 'SupplierById',
              component: Supplier,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SUPPLIERS'
              }
            },
            {
              path: 'Supplier',
              name: 'Supplier',
              component: Supplier,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SUPPLIERS'
              }
            },
            {
              path: 'SupplierList',
              name: 'SupplierList',
              component: SupplierList,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SUPPLIERS'
              }
            },

            {
              path: 'Customer/:id',
              name: 'CustomerById',
              component: Customer,
              meta: {
                moduleCode: 'CUSTOMER'
              }
            },
            {
              path: 'Customer',
              name: 'Customer',
              component: Customer,
              meta: {
                moduleCode: 'CUSTOMER'
              }
            },
            {
              path: 'CustomerList',
              name: 'CustomerList',
              component: CustomerList,
              meta: {
                moduleCode: 'CUSTOMER'
              }
            },
            {
              path: 'Showroom',
              name: 'Showroom',
              component: Showroom,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SHOWROOMS'
              }
            },
            {
              path: 'Showroom/:id',
              name: 'ShowroomById',
              component: Showroom,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SHOWROOMS'
              }
            },
            {
              path: 'showroomList',
              name: 'showroomList',
              component: ShowroomList,
              meta: {
                moduleCode: 'MASTER_DATA',
                subModuleCode: 'SHOWROOMS'
              }
            },
            {
              path: 'ConfirmBuy',
              name: 'ConfirmBuy',
              component: ConfirmBuy,
              meta: {
                moduleCode: 'STOCK_TO_BUY'
              }
            },
            {
              path: 'ConfirmBuy/:id',
              name: 'ConfirmBuyById',
              component: ConfirmBuy,
              meta: {
                moduleCode: 'STOCK_TO_BUY'
              }
            },
            {
              path: 'ConfirmBuyList',
              name: 'ConfirmBuyList',
              component: ConfirmBuyList,
              meta: {
                moduleCode: 'STOCK_TO_BUY'
              }
            },
            {
              path: 'salesReport/:id',
              name: 'salesReportById',
              component: SalesReport,
              meta: {
                moduleCode: 'REPORT',
                subModuleCode: 'SALES_REPORT'
              }
            },
            {
              path: 'salesReportList',
              name: 'salesReportList',
              component: SalesReportList,
              meta: {
                moduleCode: 'REPORT',
                subModuleCode: 'SALES_REPORT'
              }
            },
            {
              path: 'LjReportList',
              name: 'ljReportList',
              component: LjReportList,
              meta: {
                moduleCode: 'REPORT',
                subModuleCode: 'LJ_REPORTS'
              }
            },
            {
              path: 'LjReport/:id',
              name: 'ljReportById',
              component: LjReport,
              meta: {
                moduleCode: 'REPORT',
                subModuleCode: 'LJ_REPORTS'
              }
            },


          ]
        },
        {
          path: 'general',
          redirect: '/general/profile',
          name: 'Profile',
          component: {
            render(c) { return c('router-view') }
          },
          children: [
            {
              path: 'profile',
              name: 'profile',
              component: Profile
            },


          ]
        },
      ]
    },
    {
      path: '/admins',
      redirect: '/admins',
      name: 'Admin',
      component: TheContainer,
      
    },


    {
      path: '/pages',
      redirect: '/pages/404',
      name: 'Pages',
      component: {
        render(c) { return c('router-view') }
      },
      children: [
        {
          path: '403',
          name: 'Page403',
          component: Page403
        },
           {
          path: '404',
          name: 'Page404',
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          component: Page500
        },
        {
          path: 'login',
          name: 'Login',
          component: Login
        },
        {
          path: 'register',
          name: 'Register',
          component: Register
        }
      ]
    }
  ]
}
